import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { IPermissions, TabInfo } from '../../common/utils/interfaces';
import { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import ToolsAndTechnologiesFilter from './tools-and-technologies-filter';
import NoRecords from '../../common/application/no-records';
import ToolsAndTechnologiesGrid from './tools-and-technology-grid';
import AddToolsAndTechForm from './forms/add-tools-and-tech-form';
import AddIcon from '@mui/icons-material/Add';
import { GET_SKILLS } from '../gql';
import { useAppSelector } from '../../../redux/hooks';
import TabsBarForTools from './tabs-bar-for-tools';

const tabs: TabInfo[] = [
  { label: 'SE', value: 'SE' },
  { label: 'QA', value: 'QA' },
  { label: 'BA', value: 'BA' },
  { label: 'PM', value: 'PM' },
  { label: 'DevOps', value: 'Devops' }
];

function ToolsAndTechnologies(): JSX.Element {
  const profileData = useAppSelector((state) => state.profileData.profileData);

  const { refetch, loading, data: skillsData } = useQuery(GET_SKILLS);

  const [tab, setTab] = useState('SE');

  const [needRefetch, setNeedRefetch] = useState<boolean>(false);

  const [displayName, setDisplayName] = useState<string>('');

  const [isAddToolsFormVisible, setAddToolsFormVisible] = useState(false);

  const [permissions, setPermissions] = useState<IPermissions>({
    project: { create: false, read: true, update: false, delete: false },
    project_member: { create: false, read: true, update: false, delete: false },
    tools: { create: false, read: true, update: false, delete: false }
  });

  const createToolCallback = (toolId: string): void => {
    setNeedRefetch(!needRefetch);
  };

  useEffect(() => {
    void refetch();
  }, [needRefetch]);

  useEffect(() => {}, [displayName]);

  useEffect(() => {
    if (profileData?.permissions) {
      setPermissions(profileData.permissions);
    }
  }, [profileData]);

  const handleTabChange = (
    _event: React.SyntheticEvent,
    newTab: string
  ): void => {
    setTab(newTab);
  };

  const handleScroll = (event: React.UIEvent<HTMLElement>): void => {
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;

    if (scrollHeight - scrollTop <= clientHeight + 1) {
      const currentLength = skillsData.getSkills.length;
      void handleLoadMore(currentLength);
    }
  };

  const handleLoadMore = async (skipCount: number): Promise<void> => {
    await refetch({
      pagination: {
        skip: 0,
        take: 23 + skipCount
      }
    });
  };

  return (
    <Box
      data-testid={'tools-and-technologies-container'}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        allignItems: 'flex-start',
        padding: '21px 20px',
        height: 'calc(100vh - 75px)',
        margin: '0px 22px 20px 22px',
        borderRadius: '12px',
        gap: '8px',
        backgroundColor: '#FFFFFF',
        overflow: 'hidden'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '11px',
          color: '#46464F',
          alignItems: 'flex-start'
        }}
      >
        <Typography sx={{ fontSize: '28px', fontWeight: 700 }}>
          {`Tools & Technologies`}
        </Typography>
        <Typography sx={{ fontSize: '16px', fontWeight: 500 }}>
          {'User Management > Tools & Technologies'}
        </Typography>
      </Box>

      <Box>
        <Box
          sx={{
            display: 'flex',
            marginTop: '20px',
            flexDirection: 'column',
            marginBottom: '10px'
          }}
        >
          <TabsBarForTools
            tabs={tabs}
            tab={tab}
            handleTabChange={handleTabChange}
          />
          <Box
            sx={{
              height: '1px',
              background: '#E0E2EC'
            }}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between'
          }}
        >
          <ToolsAndTechnologiesFilter
            onNameChangeCallback={(value) => setDisplayName(value)}
          />{' '}
          {permissions.tools.create && (
            <Button
              variant="contained"
              type="submit"
              onClick={() => setAddToolsFormVisible(true)}
              sx={{
                borderRadius: '100px',
                width: '130px',
                height: '40px',
                fontFamily: 'Be Vietnam Pro',
                fontSize: '14px',
                lineHeight: '20px',
                textAlign: 'center',
                fontWeight: 500,
                color: '#FFFFFF',
                backgroundColor: '#295DAB',
                textTransform: 'none',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <AddIcon sx={{ width: '18px', height: '18px' }}></AddIcon> Add New
            </Button>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems:
              skillsData?.getSkills.length === 0 ? 'center' : 'flex-start',
            overflowY: skillsData?.getSkills.length === 0 ? 'none' : 'auto',
            height: 'calc(100vh - 330px)',
            scrollPadding: '0px',
            '&::-webkit-scrollbar': {
              width: '7px'
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(217, 217, 217, 1)',
              borderRadius: '10px'
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(217, 217, 217, 0.4)',
              borderRadius: '10px'
            },
            marginTop: '10px'
          }}
          onScroll={handleScroll}
        >
          {loading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                pointerEvents: 'none'
              }}
            >
              <CircularProgress />
            </Box>
          ) : skillsData.getSkills.length !== 0 ? (
            <ToolsAndTechnologiesGrid
              toolsAndTechnologies={skillsData?.getSkills}
              editToolCallback={createToolCallback}
              searchedTool={displayName}
              tabs={tabs}
              selectedTab={tab}
              permissions={permissions}
            />
          ) : (
            <NoRecords message={'No Tools Or Technologies Available'} />
          )}
        </Box>
      </Box>
      <AddToolsAndTechForm
        open={isAddToolsFormVisible}
        handleClose={() => setAddToolsFormVisible(false)}
        id={displayName}
        comment=""
        createToolCallback={createToolCallback}
        tabs={tabs}
      />
    </Box>
  );
}

export default ToolsAndTechnologies;
